import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Building2, MapPin, Calendar, Eye, FileText, ArrowLeft } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ListingCard from '../components/ListingCard';
import { getAdvertiserProfile } from '../lib/api';
import { formatDate } from '../lib/utils';

const AdvertiserProfilePage = () => {
    const { advertiserId } = useParams();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await getAdvertiserProfile(advertiserId);
                setProfile(res.data);
            } catch (err) {
                setError(err.response?.data?.detail || 'Failed to load profile');
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [advertiserId]);

    if (loading) {
        return (
            <Layout>
                <div className="min-h-[60vh] flex items-center justify-center">
                    <div className="spinner" />
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="min-h-[60vh] flex items-center justify-center">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Profile Not Found</h2>
                        <p className="text-gray-600 mb-4">{error}</p>
                        <Link to="/listings" className="text-navy hover:underline">
                            Browse Listings
                        </Link>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="bg-gray-50 min-h-screen" data-testid="advertiser-profile-page">
                {/* Header */}
                <div className="bg-navy text-white py-12">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <Link to="/listings" className="inline-flex items-center text-gray-300 hover:text-white mb-6">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Listings
                        </Link>
                        
                        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                            <div className="w-20 h-20 bg-gold rounded-xl flex items-center justify-center">
                                <Building2 className="w-10 h-10 text-white" />
                            </div>
                            <div className="flex-1">
                                <h1 className="font-heading text-3xl font-bold mb-2">
                                    {profile.business_name}
                                </h1>
                                <div className="flex flex-wrap items-center gap-4 text-gray-300">
                                    {(profile.city || profile.state) && (
                                        <div className="flex items-center gap-1">
                                            <MapPin className="w-4 h-4" />
                                            {[profile.city, profile.state].filter(Boolean).join(', ')}
                                        </div>
                                    )}
                                    {profile.member_since && (
                                        <div className="flex items-center gap-1">
                                            <Calendar className="w-4 h-4" />
                                            Member since {formatDate(profile.member_since)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <Card>
                            <CardContent className="p-4 text-center">
                                <FileText className="w-8 h-8 text-navy mx-auto mb-2" />
                                <div className="text-2xl font-bold text-navy">{profile.total_listings}</div>
                                <p className="text-sm text-gray-500">Active Listings</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4 text-center">
                                <Eye className="w-8 h-8 text-gold mx-auto mb-2" />
                                <div className="text-2xl font-bold text-gold">{profile.total_views}</div>
                                <p className="text-sm text-gray-500">Total Views</p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Listings */}
                    <div>
                        <h2 className="font-heading text-2xl font-bold text-navy mb-6">
                            Listings by {profile.business_name}
                        </h2>
                        
                        {profile.listings.length > 0 ? (
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {profile.listings.map(listing => (
                                    <ListingCard key={listing.id} listing={listing} />
                                ))}
                            </div>
                        ) : (
                            <Card>
                                <CardContent className="p-12 text-center">
                                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-500">No active listings at the moment</p>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default AdvertiserProfilePage;
